import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, HeartPulse, UserPlus, Lock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Supervisor-only "Add a sick day" form. Backend derives supervisor from the
 * authenticated user; this modal never sends supervisor_id.
 */
export default function AddSickDayModal({ open, onOpenChange, reference, onCreated }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [manual, setManual] = useState(false);         // toggles manual entry
  const [picked, setPicked] = useState(null);          // existing contractor
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    department: "",
    site: "",
    job_role: "",
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  // reset on open
  useEffect(() => {
    if (open) {
      setQ(""); setResults([]); setManual(false); setPicked(null);
      setForm({ first_name: "", last_name: "", department: "", site: "", job_role: "", start_date: "", end_date: "", notes: "" });
    }
  }, [open]);

  // contractor search (debounced)
  useEffect(() => {
    if (!open || manual || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/supervisor/contractors`, { params: { q } });
        setResults(data || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, manual, open]);

  const days = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    const s = new Date(form.start_date); const e = new Date(form.end_date);
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  }, [form.start_date, form.end_date]);

  const departments = user?.departments || [];

  const selectContractor = (c) => {
    setPicked(c);
    setForm({
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      department: c.department || "",
      site: c.site || "",
      job_role: (c.job_roles && c.job_roles[0]) || c.job_role || "",
      start_date: form.start_date,
      end_date: form.end_date,
      notes: form.notes,
    });
    setResults([]);
    setQ(`${c.first_name} ${c.last_name}`);
  };

  const enterManualMode = () => {
    setManual(true); setPicked(null); setResults([]);
    setForm((f) => ({ ...f, first_name: "", last_name: "" }));
  };

  const submit = async () => {
    const errs = [];
    ["first_name","last_name","department","job_role","start_date","end_date"].forEach((k) => { if (!form[k]) errs.push(k.replace("_"," ")); });
    if (days <= 0) errs.push("valid date range");
    if (errs.length) { toast.error("Please fill: " + errs.join(", ")); return; }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        department: form.department,
        site: form.site || null,
        job_role: form.job_role,
        start_date: form.start_date,
        end_date: form.end_date,
        days,
        notes: form.notes || null,
        contractor_id: picked?.id || null,
      };
      const { data } = await api.post("/supervisor/sickness", payload);
      toast.success("Sickness added — Main Manager notified");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add sickness");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="add-sickness-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-rose-500"/>
            Add a sick day
            <Badge variant="destructive" className="ml-2" data-testid="sickness-badge-in-modal">SICKNESS</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground pt-1">
            The Main Manager will be notified. No email will be sent to the contractor or you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Contractor picker */}
          <div className="space-y-2">
            <Label>Search for contractor</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input
                data-testid="sickness-contractor-search"
                value={q}
                disabled={manual}
                onChange={(e) => { setQ(e.target.value); setPicked(null); }}
                placeholder="Type at least 2 letters of a first/last name…"
                className="pl-8"
              />
            </div>
            {results.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-[#141414]" data-testid="sickness-contractor-results">
                {results.slice(0, 12).map((c) => (
                  <button
                    key={c.id}
                    data-testid={`sickness-contractor-opt-${c.id}`}
                    onClick={() => selectContractor(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#1e1e1e] flex items-center justify-between border-b border-border/40 last:border-b-0"
                  >
                    <span>{c.first_name} {c.last_name}</span>
                    <span className="text-xs text-muted-foreground">{c.department}{c.site ? ` · ${c.site}` : ""}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              {picked ? (
                <span className="text-muted-foreground">Selected: <span className="text-foreground">{picked.first_name} {picked.last_name}</span> · <em>existing</em></span>
              ) : manual ? (
                <span className="text-muted-foreground"><UserPlus className="w-3 h-3 inline"/> Manual entry — contractor not in system</span>
              ) : (
                <button
                  type="button"
                  data-testid="sickness-manual-toggle"
                  onClick={enterManualMode}
                  className="text-rose-400 hover:text-rose-300 underline underline-offset-2"
                >
                  Can't find them? Enter manually
                </button>
              )}
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="First name">
              <Input data-testid="sickness-first-name" value={form.first_name} disabled={!manual && !!picked && !!form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}/>
            </Field>
            <Field label="Last name">
              <Input data-testid="sickness-last-name" value={form.last_name} disabled={!manual && !!picked && !!form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}/>
            </Field>
            <Field label="Department">
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="sickness-department"><SelectValue placeholder="Choose department"/></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d} value={d} data-testid={`sickness-dept-${d}`}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Site">
              <Select value={form.site || "__none"} onValueChange={(v) => setForm({ ...form, site: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="sickness-site"><SelectValue placeholder="Choose site (optional)"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {(reference?.sites || []).map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Job role">
              <Select value={form.job_role} onValueChange={(v) => setForm({ ...form, job_role: v })}>
                <SelectTrigger data-testid="sickness-job-role"><SelectValue placeholder="Choose role"/></SelectTrigger>
                <SelectContent>
                  {(reference?.job_roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Supervisor">
              <div
                data-testid="sickness-supervisor-readonly"
                className="flex items-center gap-2 h-10 px-3 rounded-md bg-[#141414] border border-border text-sm cursor-not-allowed"
                title="Assigned automatically from your account"
              >
                <Lock className="w-3.5 h-3.5 text-muted-foreground"/>
                <span className="truncate">{user?.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">You</span>
              </div>
            </Field>
            <Field label="Request type">
              <div className="flex items-center h-10 px-3 rounded-md bg-rose-950/40 border border-rose-800 text-sm text-rose-200 font-medium">
                <HeartPulse className="w-3.5 h-3.5 mr-2"/> Sickness (locked)
              </div>
            </Field>
            <Field label="Start date">
              <Input type="date" data-testid="sickness-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}/>
            </Field>
            <Field label="End date">
              <Input type="date" data-testid="sickness-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}/>
            </Field>
            <Field label="Number of days">
              <div data-testid="sickness-days" className="flex items-center h-10 px-3 rounded-md bg-[#141414] border border-border text-sm font-medium">
                {days} {days === 1 ? "day" : "days"}
              </div>
            </Field>
          </div>

          <Field label="Notes (optional)">
            <Textarea data-testid="sickness-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any relevant context…"/>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sickness-cancel">Cancel</Button>
          <Button
            data-testid="sickness-submit"
            onClick={submit}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-500 text-white"
          >
            <HeartPulse className="w-4 h-4 mr-1.5"/>
            {busy ? "Adding…" : "Add sick day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
